import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getCommonListSlice } from '../../../redux/common_list/slice';
import {
  type CommonListGetConfig,
  type CommonListGetParams,
  commonGetList,
} from '../../../redux/common_list/thunks';
import type { CommonListAppState } from '../../../redux/common_list/types';
import { createEntitiesDenormalizer } from '../../../redux/entities/normalizers';
import type { StoreEntityKey } from '../../../redux/entities/types';
import { mergeNormalizedDataInjection } from '../../../redux/middlewares/http_normalizer/merge_normalized_data_injection';
import type {
  EndpointResponseNormalized,
  NormalizedDataInjection,
} from '../../../redux/middlewares/http_normalizer/types';
import { useAppDispatch, useAppSelector } from '../../../redux/redux_hooks';
import type { AppAction, AppDispatch } from '../../../redux/types';
import { getObjectStableKey } from '../../../utils/get_object_stable_key';
import { statusShortcuts } from './status_shortcuts';

type GenericListResponseBody = {
  page: number;
  pages: number;
  per_page: number;
  total: number;
  objects: unknown[];
};

type ActionCreator<T, A> = (payload: T) => A;

function useAction<T, A extends AppAction>(
  action: ActionCreator<T, A>,
  dispatch: AppDispatch,
) {
  return useCallback(
    (payload: T) => dispatch(action(payload)),
    [action, dispatch],
  );
}

type UseEntityListOptions<
  EntityType extends StoreEntityKey,
  ListName extends string,
  GetResult extends EndpointResponseNormalized,
> = {
  config?: CommonListGetConfig;
  entityType: EntityType;
  listName: ListName;
  customStatePath?: string[];
  getList: (
    dispatch: AppDispatch,
    params: CommonListGetParams,
  ) => Promise<GetResult>;
  enabled?: boolean;
  fetchAllPages?: boolean;
};

export function useEntityList<
  EntityType extends StoreEntityKey,
  ListName extends string,
  GetResult extends EndpointResponseNormalized,
>({
  config,
  entityType,
  listName,
  customStatePath,
  getList,
  enabled = true,
  fetchAllPages,
}: UseEntityListOptions<EntityType, ListName, GetResult>) {
  const fetchInitiated = useRef(false);
  const prevEnabled = useRef(enabled);

  // If enabled toggles from false -> true, we must reset the initiated flag
  // This runs during render, ensuring the derived state is correct immediately
  if (enabled && !prevEnabled.current) {
    fetchInitiated.current = false;
  }
  prevEnabled.current = enabled;

  const denormalizer = useMemo(
    () => createEntitiesDenormalizer(entityType),
    [entityType],
  );

  const dispatch = useAppDispatch();
  const { actions } = getCommonListSlice(listName);

  const listStatePath = customStatePath ?? [listName];

  const listState = useSelector((state: Record<string, any>) => {
    // biome-ignore lint/plugin: the list slice is looked up by a caller-supplied path, so the walk cannot stay typed.
    return listStatePath.reduce((subState, path) => subState?.[path], state) as
      | CommonListAppState
      | undefined;
  });
  if (!listState) {
    throw new Error(`List state not found for ${listName}`);
  }

  // Keep the freshest list state available to event-driven callbacks (e.g.
  // fetchNextPage fired from a scroll handler) so they never act on a stale
  // render snapshot of pagination / fetch status.
  const listStateRef = useRef(listState);
  listStateRef.current = listState;
  const paginationType = config?.paginationType ?? 'infinite';

  const {
    ids: listStateIds = [],
    isLoading,
    status,
    fetchStatus: reduxFetchStatus,
    error,
    nextSearchAfter,
    ...partialListState
  } = listState;

  // Subscribe to the denormalized list itself (reference-stable while its
  // values are unchanged) instead of the whole entities state, so unrelated
  // entity updates don't re-render the consumer.
  const entities = useAppSelector((state) =>
    denormalizer(state.entities, listStateIds),
  );

  // If we are enabled, but haven't triggered the function yet,
  // we force 'fetching' to bridge the gap until Redux updates.
  // Once fetchInitiated is true, we strictly trust Redux.
  const fetchStatus =
    enabled && !fetchInitiated.current ? 'fetching' : reduxFetchStatus;

  const getListAllPages = useCallback(
    async (
      appDispatch: AppDispatch,
      params: CommonListGetParams,
    ): Promise<GetResult> => {
      const objects: unknown[] = [];
      let meta: NormalizedDataInjection = {};

      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await getList(appDispatch, {
          ...params,
          page: currentPage,
        });

        // biome-ignore lint/plugin: every paginated list endpoint returns this shape, but the generic Result cannot express it.
        const body = response.body as GenericListResponseBody;
        meta = mergeNormalizedDataInjection(meta, response.meta);
        objects.push(...body.objects);
        totalPages = body.pages;
        currentPage++;
      } while (currentPage <= totalPages);

      // biome-ignore lint/plugin: the accumulated pages are returned in the same shape the caller's Result declares.
      return { body: { objects }, meta } as GetResult;
    },
    [getList],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: config dependency as string
  const getEntities = useCallback(
    (params: CommonListGetParams) => {
      return commonGetList({
        listName,
        dispatch,
        getList: fetchAllPages ? getListAllPages : getList,
        params,
        config,
      });
    },
    [
      getObjectStableKey(config),
      getList,
      listName,
      fetchAllPages,
      getListAllPages,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: sort dependency as string
  const fetchCurrentPage = useCallback(() => {
    if (!enabled) {
      return;
    }
    fetchInitiated.current = true;

    getEntities({
      page: listState.page,
      per_page: listState.perPage,
      sort: listState.sort,
    });
  }, [
    getEntities,
    listState.page,
    listState.perPage,
    getObjectStableKey(listState.sort),
    enabled,
  ]);

  const fetchNextPage = useCallback(() => {
    const currentState = listStateRef.current;
    if (
      !enabled ||
      currentState.fetchStatus === 'fetching' ||
      currentState.error
    ) {
      return;
    }
    if (paginationType === 'cursor') {
      if (!currentState.nextSearchAfter) {
        return;
      }
      fetchInitiated.current = true;
      getEntities({
        per_page: currentState.perPage,
        sort: currentState.sort,
        search_after: currentState.nextSearchAfter,
      });
      return;
    }
    if (currentState.pagination.page >= currentState.pagination.pages) {
      return;
    }
    fetchInitiated.current = true;
    getEntities({
      page: currentState.pagination.page + 1,
      per_page: currentState.perPage,
      sort: currentState.sort,
    });
  }, [getEntities, paginationType, enabled]);

  useEffect(() => {
    fetchCurrentPage();
  }, [fetchCurrentPage]);

  useEffect(() => {
    return () => {
      dispatch(actions.reset());
    };
  }, [actions]);

  const setPage = useAction(actions.setPage, dispatch);
  const setPerPage = useAction(actions.setPerPage, dispatch);
  const setSort = useAction(actions.setSort, dispatch);
  const appendId = useAction(actions.appendId, dispatch);
  const appendIds = useAction(actions.appendIds, dispatch);
  const deleteId = useAction(actions.deleteId, dispatch);
  const deleteIds = useAction(actions.deleteIds, dispatch);
  const prependId = useAction(actions.prependId, dispatch);
  const setIds = useAction(actions.setIds, dispatch);
  const reset = useAction(actions.reset, dispatch);

  return {
    entities,
    ...partialListState,
    status,
    fetchStatus,
    error,
    ...statusShortcuts(status, fetchStatus),
    setPage,
    setPerPage,
    setSort,
    appendId,
    appendIds,
    deleteId,
    deleteIds,
    prependId,
    setIds,
    reset,
    refetch: fetchCurrentPage,
    fetchNextPage,
    nextSearchAfter,
  };
}
